<script setup>
import { computed, onMounted, ref, nextTick } from 'vue';
import { picoSearch } from '@scmmishra/pico-search';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useRouter, useRoute } from 'vue-router';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import PageLayout from 'dashboard/components-next/assistant/PageLayout.vue';
import KnowledgeBaseCard from 'dashboard/components-next/assistant/shared/KnowledgeBaseCard.vue';
import CreateKnowledgeBaseDialog from 'dashboard/components-next/assistant/pageComponents/knowledgeBase/CreateKnowledgeBaseDialog.vue';
import DeleteDialog from 'dashboard/components-next/assistant/pageComponents/DeleteDialog.vue';

const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('assistantKnowledgeBases/getUIFlags');
const knowledgeBases = useMapGetter('assistantKnowledgeBases/getRecords');
const knowledgeBasesMeta = useMapGetter('assistantKnowledgeBases/getMeta');
const isFetching = computed(() => uiFlags.value.fetchingList);

// Поиск по загруженным записям (клиентская фильтрация — под масштаб SMB).
const searchQuery = ref('');
const filteredKnowledgeBases = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return knowledgeBases.value;
  return picoSearch(knowledgeBases.value, query, ['name', 'description']);
});

const selectedKnowledgeBase = ref(null);
const showCreateDialog = ref(false);
const createKnowledgeBaseDialog = ref(null);
const deleteKnowledgeBaseDialog = ref(null);

const fetchKnowledgeBases = (page = 1) => {
  store.dispatch('assistantKnowledgeBases/get', { page });
};

const handleCreate = () => {
  showCreateDialog.value = true;
  nextTick(() => createKnowledgeBaseDialog.value.dialogRef.open());
};

const handleCreateDialogClose = () => {
  showCreateDialog.value = false;
};

const handleAction = ({ action, id }) => {
  selectedKnowledgeBase.value = knowledgeBases.value.find(
    knowledgeBase => id === knowledgeBase.id
  );
  nextTick(() => {
    if (action === 'delete') {
      deleteKnowledgeBaseDialog.value.dialogRef.open();
    }
  });
};

// Р9.3: клик по карточке открывает страницу базы знаний.
const handleNavigate = id => {
  router.push({
    name: 'assistant_knowledge_bases_show',
    params: { accountId: route.params.accountId, knowledgeBaseId: id },
  });
};

const onPageChange = page => fetchKnowledgeBases(page);

const onCreateSuccess = () => {
  fetchKnowledgeBases(1);
};

const onDeleteSuccess = () => {
  if (
    knowledgeBases.value?.length === 0 &&
    knowledgeBasesMeta.value?.page > 1
  ) {
    onPageChange(knowledgeBasesMeta.value.page - 1);
  } else {
    fetchKnowledgeBases(knowledgeBasesMeta.value?.page || 1);
  }
};

onMounted(() => fetchKnowledgeBases());
</script>

<template>
  <PageLayout
    v-model:search-query="searchQuery"
    :header-title="$t('ASSISTANT.KNOWLEDGE_BASES.HEADER')"
    :description="$t('ASSISTANT.KNOWLEDGE_BASES.PAGE_DESCRIPTION')"
    :search-placeholder="$t('ASSISTANT.KNOWLEDGE_BASES.SEARCH_PLACEHOLDER')"
    :empty-message="$t('ASSISTANT.KNOWLEDGE_BASES.EMPTY.TITLE')"
    :button-label="$t('ASSISTANT.KNOWLEDGE_BASES.ADD_NEW')"
    :button-policy="['administrator']"
    :total-count="knowledgeBasesMeta.totalCount"
    :current-page="knowledgeBasesMeta.page"
    :is-fetching="isFetching"
    :is-empty="!knowledgeBases.length"
    :show-pagination-footer="!isFetching && !!knowledgeBases.length"
    :feature-flag="FEATURE_FLAGS.AI_ASSISTANT"
    @update:current-page="onPageChange"
    @click="handleCreate"
  >
    <template #body>
      <div
        v-if="!filteredKnowledgeBases.length"
        class="py-6 text-sm text-center text-n-slate-11"
      >
        {{ $t('ASSISTANT.KNOWLEDGE_BASES.SEARCH_EMPTY') }}
      </div>
      <div v-else class="flex flex-col gap-4">
        <KnowledgeBaseCard
          v-for="knowledgeBase in filteredKnowledgeBases"
          :id="knowledgeBase.id"
          :key="knowledgeBase.id"
          :name="knowledgeBase.name"
          :description="knowledgeBase.description || ''"
          :kind="knowledgeBase.kind"
          :documents-count="knowledgeBase.documents_count"
          :responses-count="knowledgeBase.responses_count"
          :pending-embeddings-count="knowledgeBase.pending_embeddings_count"
          :updated-at="knowledgeBase.updated_at"
          @action="handleAction"
          @navigate="handleNavigate"
        />
      </div>
    </template>

    <CreateKnowledgeBaseDialog
      v-if="showCreateDialog"
      ref="createKnowledgeBaseDialog"
      @create-success="onCreateSuccess"
      @close="handleCreateDialogClose"
    />
    <DeleteDialog
      v-if="selectedKnowledgeBase"
      ref="deleteKnowledgeBaseDialog"
      :entity="selectedKnowledgeBase"
      type="KnowledgeBases"
      translation-key="KNOWLEDGE_BASES"
      @delete-success="onDeleteSuccess"
    />
  </PageLayout>
</template>
